<template>
  <div class="flex flex-wrap sticky -top-10 bg-white z-10 gap-x-2 border-b border-slate-300 shadow-sm">
    <EditorMenuButton
      @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      :active="editor.isActive('heading', { level: 1 })"
      title="Heading 1"
    >
      <H1Icon class="w-5 h-5" alt="h1" />
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      :active="editor.isActive('heading', { level: 2 })"
      title="Heading 2"
    >
      <H2Icon class="w-5 h-5" alt="h2" />
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      :active="editor.isActive('heading', { level: 3 })"
      title="Heading 3"
    >
      <H3Icon class="w-5 h-5" alt="h3" />
    </EditorMenuButton>
    <div class="border-r border-slate-300"></div>
    <!-- ALIGN -->
    <EditorMenuButton
      @click="editor.chain().focus().setTextAlign('left').run()"
      :active="editor.isActive({ textAlign: 'left' })"
      title="Align Left"
    >
      <Bars3BottomLeftIcon class="w-5 h-5" alt="left align" />
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().setTextAlign('center').run()"
      :active="editor.isActive({ textAlign: 'center' })"
      title="Align Center"
    >
      <Bars3Icon class="w-5 h-5" alt="center align" />
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().setTextAlign('right').run()"
      :active="editor.isActive({ textAlign: 'right' })"
      title="Align Right"
    >
      <Bars3BottomRightIcon class="w-5 h-5" alt="right align" />
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().setTextAlign('justify').run()"
      :active="editor.isActive({ textAlign: 'justify' })"
      title="Justify"
    >
      <Bars4Icon class="w-5 h-5" alt="justify text" />
    </EditorMenuButton>
    <div class="border-r border-slate-300"></div>
    <EditorMenuButton
      @click="editor.chain().focus().toggleOrderedList().run()"
      :active="editor.isActive('orderedList')"
      title="Ordered List"
    >
      <NumberedListIcon class="w-5 h-5" alt="numbered list" />
    </EditorMenuButton>

    <EditorMenuButton @click="editor.chain().focus().toggleBulletList().run()" :active="editor.isActive('bulletList')" title="Bullet List">
      <ListBulletIcon class="w-5 h-5" alt="bullet list" />
    </EditorMenuButton>

    <div class="border-r border-slate-300"></div>
    <EditorMenuButton @click="editor.chain().focus().toggleBold().run()" :active="editor.isActive('bold')" title="Bold">
      b
    </EditorMenuButton>
    <EditorMenuButton @click="editor.chain().focus().toggleItalic().run()" :active="editor.isActive('italic')" title="Italic">
      <em>i</em>
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().toggleStrike().run()"
      :active="editor.isActive('strike')"
      title="Strikethrough"
      class="line-through"
    >
      s
    </EditorMenuButton>
    <EditorMenuButton @click="editor.chain().focus().toggleUnderline().run()" :active="editor.isActive('underline')" title="Underline"
      ><u>u</u></EditorMenuButton
    >
    <div class="border-r border-slate-300"></div>
    <EditorMenuButton @click="editor.chain().focus().addCondition('templateVariableName').run()" :active="false" title="Add Condition">
      <BoltIcon class="w-5 h-5" alt="condition" />
    </EditorMenuButton>
    <EditorMenuButton
      v-if="editor.can().removeCondition()"
      @click="editor.chain().focus().removeCondition().run()"
      :active="editor.isActive('condition')"
      title="Remove Condition"
    >
      <BoltSlashIcon class="w-5 h-5" alt="remove condition" />
    </EditorMenuButton>
    <EditorMenuButton @click="editor.chain().focus().addSection().run()" :active="false" title="Add Section">
      <SquaresPlusIcon class="w-5 h-5" alt="section" />
    </EditorMenuButton>
    <EditorMenuButton
      @click="editor.chain().focus().splitSection().run()"
      :disabled="!canSplitSection"
      :active="false"
      title="Split Section"
    >
      <Squares2X2Icon class="w-5 h-5" alt="split section" />
    </EditorMenuButton>
    <EditorMenuButton @click="emit('save')" :active="false" title="Save" class="flex items-center gap-2 text-secondary font-bold">
      <FloppyDiskIcon class="w-5 h-5" /> SAVE
    </EditorMenuButton>
    <div class="border-r border-slate-300"></div>
    <EditorMenuButton
      @click="editor.commands.openTemplateVariableEditor()"
      :active="false"
      title="Add Template Variable"
    ></EditorMenuButton>
    <div class="border-r border-slate-300"></div>
    <EditorMenuButton @click="emit('showHTML')" :active="false" title="Show HTML">
      <CodeBracketIcon class="w-5 h-5" alt="show html" />
    </EditorMenuButton>
    <EditorMenuButton @click="emit('showJSON')" :active="false" title="Show JSON">
      <CodeBracketIcon class="w-5 h-5" alt="show json" />
    </EditorMenuButton>
  </div>
</template>

<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
import EditorMenuButton from './EditorMenuButton.vue'
import { computed } from 'vue'

import {
  H1Icon,
  H2Icon,
  H3Icon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3Icon,
  ListBulletIcon,
  BoltIcon,
  SquaresPlusIcon,
  Squares2X2Icon,
  Bars4Icon,
  CodeBracketIcon,
} from '@heroicons/vue/24/solid'
import { BoltSlashIcon, NumberedListIcon } from '@heroicons/vue/24/outline'
import { FloppyDiskIcon } from '@sidekickicons/vue/24/outline'

const { editor } = defineProps<{ editor: Editor }>()
const emit = defineEmits<{
  save: []
  showHTML: []
  showJSON: []
}>()

const canSplitSection = computed(() => {
  if (!editor) return false

  const { selection } = editor.state
  const { $from } = selection

  // Check if cursor is inside a section
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth)
    if (node.type.name === 'section') {
      // Check if cursor is not at the very beginning or end of the section
      const sectionStart = $from.start(depth)
      const sectionEnd = $from.end(depth)
      const cursorPos = $from.pos

      // Allow split if cursor is not at the boundaries and section has content
      return cursorPos > sectionStart && cursorPos < sectionEnd && node.content.size > 0
    }
  }

  return false
})
</script>
